/**
 * ESG SaaS Platform - Scope 3 Submissions API Service
 *
 * API service for Scope 3 file-based submission workflow.
 * Users upload data files, admins calculate externally and submit results.
 */

import { api, tokenManager, API_BASE_URL } from './api';

// ============================================================================
// Types
// ============================================================================

export type Scope3SubmissionStatus = 'pending' | 'completed';

export interface Scope3Category {
  number: number;
  name: string;
}

export interface Scope3TemplateInfo {
  category: number;
  name: string;
  description: string;
  columns: Array<{
    name: string;
    type: string;
    required: boolean;
    description?: string;
    values?: string[];
  }>;
}

export interface Scope3Submission {
  id: string;
  organization_id: string;
  facility_id: string;
  facility_name: string;
  category_number: number;
  category_name: string;
  reporting_period: string;
  file_path: string;
  original_filename: string;
  file_size_bytes: number;
  file_content_type?: string;
  status: Scope3SubmissionStatus;
  result_tco2e?: number;
  result_notes?: string;
  calculated_by?: string;
  calculated_at?: string;
  created_by: string;
  created_at: string;
  updated_at?: string;
}

export interface Scope3SubmissionCreate {
  facility_id: string;
  category_number: number;
  reporting_period: string;
  file: File;
}

export interface Scope3SubmissionResultCreate {
  result_tco2e: number;
  result_notes?: string;
}

export interface Scope3SubmissionListResponse {
  submissions: Scope3Submission[];
  total: number;
  limit: number;
  offset: number;
  pending_count: number;
  completed_count: number;
}

export interface Scope3CategorySummary {
  category_number: number;
  category_name: string;
  total_submissions: number;
  completed_submissions: number;
  pending_submissions: number;
  total_tco2e: number;
}

export interface Scope3SubmissionSummary {
  total_tco2e: number;
  total_submissions: number;
  pending_count: number;
  completed_count: number;
  by_category: Scope3CategorySummary[];
  reporting_period?: string;
}

// Query parameters
interface ListParams {
  category?: number;
  facility_id?: string;
  status?: Scope3SubmissionStatus;
  reporting_period?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

function buildQuery(params: ListParams): string {
  const query = new URLSearchParams();
  if (params.category !== undefined) query.append('category', params.category.toString());
  if (params.facility_id) query.append('facility_id', params.facility_id);
  if (params.status) query.append('status', params.status);
  if (params.reporting_period) query.append('reporting_period', params.reporting_period);
  if (params.limit !== undefined) query.append('limit', params.limit.toString());
  if (params.offset !== undefined) query.append('offset', params.offset.toString());
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

// ============================================================================
// Service Functions
// ============================================================================

export const scope3SubmissionService = {
  // ================== TEMPLATES ==================

  /**
   * List all available Scope 3 category templates
   */
  listTemplates: () =>
    api.get<{ templates: Scope3TemplateInfo[] }>('/scope3-submissions/templates'),

  /**
   * Get template info for a specific category
   */
  getTemplateInfo: (category: number) =>
    api.get<Scope3TemplateInfo>(`/scope3-submissions/templates/${category}`),

  /**
   * Download Excel template for a specific category
   * Returns a blob URL for the downloaded file
   */
  downloadTemplate: async (category: number): Promise<{ url: string; filename: string }> => {
    const token = tokenManager.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/scope3-submissions/templates/${category}`, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to download template');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `scope3_category_${category}_template.xlsx`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    return { url, filename };
  },

  // ================== SUBMISSIONS ==================

  /**
   * Upload a new Scope 3 submission file
   */
  uploadSubmission: async (data: Scope3SubmissionCreate): Promise<Scope3Submission> => {
    const token = tokenManager.getAccessToken();

    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('facility_id', data.facility_id);
    formData.append('category_number', data.category_number.toString());
    formData.append('reporting_period', data.reporting_period);

    const response = await fetch(`${API_BASE_URL}/scope3-submissions/`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to upload submission');
    }

    return response.json();
  },

  /**
   * List submissions with optional filters
   */
  listSubmissions: (params: ListParams = {}) =>
    api.get<Scope3SubmissionListResponse>(`/scope3-submissions/${buildQuery(params)}`),

  /**
   * Get a single submission by ID
   */
  getSubmission: (submissionId: string) =>
    api.get<Scope3Submission>(`/scope3-submissions/${submissionId}`),

  /**
   * Delete a submission
   */
  deleteSubmission: (submissionId: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/scope3-submissions/${submissionId}`),

  // ================== ADMIN OPERATIONS ==================

  /**
   * Download the uploaded file for a submission (admin only)
   * Returns a blob URL for the downloaded file
   */
  downloadSubmissionFile: async (submissionId: string): Promise<{ url: string; filename: string }> => {
    const token = tokenManager.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/scope3-submissions/${submissionId}/download`, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to download file');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'submission_file';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    return { url, filename };
  },

  /**
   * Submit calculation result for a submission (admin only)
   */
  submitResult: (submissionId: string, result: Scope3SubmissionResultCreate) =>
    api.post<Scope3Submission>(`/scope3-submissions/${submissionId}/result`, result),

  // ================== ANALYTICS ==================

  /**
   * Get summary of all Scope 3 submissions with totals by category
   */
  getSummary: (reportingPeriod?: string) => {
    const query = reportingPeriod ? `?reporting_period=${reportingPeriod}` : '';
    return api.get<Scope3SubmissionSummary>(`/scope3-submissions/summary${query}`);
  },

  /**
   * Get list of all 15 Scope 3 categories
   */
  getCategories: () =>
    api.get<{ categories: Scope3Category[] }>('/scope3-submissions/categories/list'),
};

// ============================================================================
// Constants - All 15 GHG Protocol Scope 3 Categories
// ============================================================================

export const SCOPE3_CATEGORIES: Scope3Category[] = [
  { number: 1, name: 'Purchased Goods and Services' },
  { number: 2, name: 'Capital Goods' },
  { number: 3, name: 'Fuel and Energy Related Activities' },
  { number: 4, name: 'Upstream Transportation and Distribution' },
  { number: 5, name: 'Waste Generated in Operations' },
  { number: 6, name: 'Business Travel' },
  { number: 7, name: 'Employee Commuting' },
  { number: 8, name: 'Upstream Leased Assets' },
  { number: 9, name: 'Downstream Transportation and Distribution' },
  { number: 10, name: 'Processing of Sold Products' },
  { number: 11, name: 'Use of Sold Products' },
  { number: 12, name: 'End-of-Life Treatment of Sold Products' },
  { number: 13, name: 'Downstream Leased Assets' },
  { number: 14, name: 'Franchises' },
  { number: 15, name: 'Investments' },
];

/**
 * Get category name by number
 */
export function getCategoryName(categoryNumber: number): string {
  const category = SCOPE3_CATEGORIES.find(c => c.number === categoryNumber);
  return category?.name || `Category ${categoryNumber}`;
}
